import groupBy from 'lodash/groupBy';
import Datasheet from 'react-datasheet';
import flatten from 'lodash/flatten';

import { User, userFactory } from '@/types/user';
import { isAscLang } from '@/settings/i18n';
import { filterEmptyUser } from './utils';

type Cell = Datasheet.Cell<Cell>;
type Changes = Datasheet.CellsChangedArgs<Cell> | Datasheet.CellAdditionsArgs;

/**
 * 現在の受講者データと変更データを元に、更新後の受講者データを生成する
 */
export const buildUpdateUsers = (
  users: User[],
  changes: Changes,
  colSize: number,
): User[] => {
  let builtUsers = [...users];

  // 変更データをフィルタリング
  const cleandChanges = getCleanChanges(changes, colSize);

  // 変更データを元に既存の受講者データを変更
  cleandChanges.forEach(({ row, col, value }) => {
    const trimmedValue = String(value).trim() || '';

    if (!builtUsers[row - 1]) {
      builtUsers[row - 1] = userFactory();
    }

    switch (col) {
      case isAscLang() ? 1 : 2:
        builtUsers[row - 1].firstName = trimmedValue;
        break;
      case isAscLang() ? 2 : 1:
        builtUsers[row - 1].lastName = trimmedValue;
        break;
      case isAscLang() ? 3 : 4:
        builtUsers[row - 1].kanaFirstName = trimmedValue;
        break;
      case isAscLang() ? 4 : 3:
        builtUsers[row - 1].kanaLastName = trimmedValue;
        break;
      case 5:
        builtUsers[row - 1].email = trimmedValue;
        break;
    }
  });

  // 全てのセルが空となった行を削除
  const allRowsCount = builtUsers.length;
  builtUsers = filterEmptyUser(builtUsers);

  // 削除した分の空行を追加
  const deletedRowCount = allRowsCount - builtUsers.length;
  for (let i = 0; i < deletedRowCount; i++) {
    builtUsers.push(userFactory());
  }

  return builtUsers;
};

/**
 * React-Datasheetの変更から空行を削除し、行番号を振り直して返す
 * ただしN行5列が全て空白の場合(カーソル操作での行の削除)、空行の削除は行わない
 */
export const getCleanChanges = (changes: Changes, colSize: number): Changes => {
  // 行毎にセル単位の変更をグループ化
  const changesGroupByRow = Object.values(groupBy(changes, 'row'));

  // 1行5列全て空だった場合に空行と判定する
  const isEmptyRow = (cells: Changes) =>
    cells.length === colSize && cells.every((cell) => !cell.value);

  // 全ての行が空行の場合、空行を削除せずにそのまま返す
  if (changesGroupByRow.every((cells) => isEmptyRow(cells))) {
    return changes;
  }

  // 空行データを削除し先頭のデータの行番号を起点として行番号を振り直し
  const firstChangeRowNum = changes[0]?.row || 1;
  const filteredRows = changesGroupByRow
    .filter((cells, i) => {
      if (isEmptyRow(cells)) {
        return false;
      }
      // WindowsのExcelコピペ対応。2行以上の変更かつ、最終行の変更が1つで空値の場合、削除
      // https://github.com/globis-org/glopla-enterprise/issues/860
      if (
        changesGroupByRow.length >= 2 &&
        i === changesGroupByRow.length - 1 &&
        cells.length === 1 &&
        !cells[0].value
      ) {
        return false;
      }
      return true;
    })
    .map((cells, i) =>
      cells.map((cell) => ({
        ...cell,
        row: firstChangeRowNum + i,
      })),
    );

  // 行のグループ化を解除して返す
  return flatten(filteredRows);
};
